/**
 * RFC-158: decide whether an auth/function error means "the server is having
 * trouble" (transient — keep the user signed in and retry) or "your session is
 * really gone" (fatal — sign out).
 */
export type AuthErrorKind = 'transient' | 'fatal';

const FATAL_CODES = new Set([
  'refresh_token_not_found',
  'refresh_token_already_used',
  'invalid_grant',
  'session_not_found',
  'user_not_found',
  'user_banned',
  'bad_jwt',
]);

const TRANSIENT_PATTERN = /network|fetch|timeout|timed out|failed to fetch|load failed|econn|503|502|504|gateway|unavailable|upstream/i;

export function classifyAuthError(err: unknown): AuthErrorKind {
  if (!err) return 'transient';
  const e = err as { name?: string; code?: string; status?: number; message?: string };

  if (e.name === 'AuthRetryableFetchError') return 'transient';
  if (e.code && FATAL_CODES.has(e.code)) return 'fatal';
  if (typeof e.status === 'number') {
    if (e.status === 0 || e.status === 429 || e.status >= 500) return 'transient';
    if (e.status === 400 || e.status === 401 || e.status === 403) return 'fatal';
  }
  const msg = e.message ?? String(err);
  if (/refresh token|invalid grant|jwt expired|not found|session missing/i.test(msg)) return 'fatal';
  if (TRANSIENT_PATTERN.test(msg)) return 'transient';
  // Unknown errors: be conservative and keep the user signed in.
  return 'transient';
}

/** Backoff schedule for reconnect attempts (ms). Caps at the last value. */
export const RECONNECT_BACKOFF_MS = [5_000, 15_000, 30_000, 60_000];

export function reconnectDelay(attempt: number): number {
  return RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)];
}
